import { createFileRoute, Link } from "@tanstack/react-router";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL = "https://vendingmachinefree.com/free-vending-machine-placement";
const TITLE = "Free Vending Machine Placement — $0 Install & Service";
const DESC = "Get FREE vending machine placement for your US business. $0 install, $0 restocking, $0 repairs. Nationwide coverage in all 50 states.";

const FAQS = [
  { q: "Is vending machine placement really free?", a: "Yes. Qualifying US businesses receive the machine, delivery, install, restocking, and repairs at $0 cost. We earn revenue from product sales." },
  { q: "What does free vending placement include?", a: "Machine supply, delivery, installation, weekly restocking, cash collection, cleaning, and all repairs — 100% managed by our local operator network." },
  { q: "Who qualifies for free vending machine placement?", a: "Warehouses, offices, schools, hospitals, hotels, gyms, manufacturing plants, logistics hubs, retail, and government buildings across the USA typically qualify." },
  { q: "How fast can a free machine be installed?", a: "Most sites are surveyed within 48 hours and machines installed within 1–2 weeks. We aim to respond to every application in 4–6 hours." },
];

export const Route = createFileRoute("/free-vending-machine-placement")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Free Vending Machine Placement",
          provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
          areaServed: { "@type": "Country", name: "United States" },
          serviceType: "Free Vending Machine Placement",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
            { "@type": "ListItem", position: 2, name: "Free Vending Machine Placement", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-steel text-white py-16">
        <div className="container-x">
          <h1 className="font-display text-4xl md:text-5xl uppercase">Free Vending Machine Placement — $0 Install, $0 Service, $0 Hassle</h1>
          <p className="mt-4 max-w-3xl text-lg opacity-90">
            Businesses across the USA can get FREE vending machine placement with no cost, no contracts, and no maintenance fees. We supply, install, stock, and service your machines — you simply enjoy the convenience.
          </p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("free_vending_machine_placement_hero")} className="inline-block mt-6 bg-safety px-6 py-3 font-semibold uppercase">Apply Now</Link>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl uppercase">Why Businesses Choose Free Placement</h2>
            <ul className="mt-4 space-y-2 list-disc pl-5">
              <li>$0 installation</li><li>$0 servicing</li><li>$0 repairs</li>
              <li>$0 restocking</li><li>Modern machines</li>
              <li>Fast operator response (4–6 hrs)</li><li>Nationwide coverage</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase">Industries We Serve</h2>
            <ul className="mt-4 grid grid-cols-2 gap-1 list-disc pl-5">
              {([
                ["Warehouses","warehouses-manufacturing"],
                ["Offices","offices-corporate"],
                ["Schools","schools-universities"],
                ["Hospitals","hospitals-clinics"],
                ["Hotels","hotels-hospitality"],
                ["Gyms","gyms-fitness-centers"],
                ["Manufacturing","warehouses-manufacturing"],
                ["Logistics","warehouses-manufacturing"],
                ["Airports","airports-aviation"],
                ["Military Bases","military-bases"],
                ["Mines","mines-mining-sites"],
              ] as const).map(([label,slug])=>(
                <li key={label}><Link className="hover:text-safety" to="/industries/$industry" params={{industry:slug}}>{label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Machine Types Available</h2>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {["Drink machines","Snack machines","Combo machines","Coffee machines","Food machines","Ice machines"].map(m=>(
              <Link key={m} to="/vending-machines" className="border p-4 hover:border-safety">{m}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">How Free Placement Works</h2>
          <ol className="mt-4 space-y-2 list-decimal pl-5 max-w-2xl">
            <li>Submit your location</li>
            <li>We assess your business needs</li>
            <li>We install machines at no cost</li>
            <li>We stock and service them weekly</li>
            <li>You enjoy a fully managed vending solution</li>
          </ol>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Nationwide Coverage — All 50 States</h2>
          <p className="mt-3">Free vending placement available across the entire USA. Find your state:</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1 text-sm">
            {STATES_DATA.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(s=>(
              <Link key={s.code} to="/states/$state" params={{state:s.slug}} className="hover:text-safety">{s.name}</Link>
            ))}
          </div>
          <p className="mt-6 text-sm">
            See our <Link to="/free-vending-machines-usa" className="text-safety underline">USA overview</Link>, <Link to="/vending-machine-placement" className="text-safety underline">general placement service</Link>, or <Link to="/coverage" className="text-safety underline">interactive coverage map</Link>.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-2xl uppercase">FAQs</h2>
          <div className="mt-4 space-y-4">
            {FAQS.map(f=>(
              <div key={f.q}><h3 className="font-semibold">{f.q}</h3><p className="text-muted-foreground">{f.a}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel text-white py-12 text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Apply for Free Placement</h2>
          <p className="mt-2 opacity-90">Get FREE vending machines for your business — $0 install & full service.</p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("free_vending_machine_placement_footer")} className="inline-block mt-6 bg-safety px-8 py-3 font-semibold uppercase">Apply Now</Link>
        </div>
      </section>
    </div>
  );
}
